// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { goalActions, sessionActions } from '../../../../../../store';
import { removeKey } from '../../../../../../utils/common';
import { SessionGoalTimingType } from '../../../../../../utils/types';
import {
  FHButton,
  FHChip,
  FHItemSelectDialog,
  FHWarning,
  FProgressBar,
} from '../../../../../../components/base';
import GoalPracticed from './GoalPracticed';
import { capitalizeFirstLetter } from '../../../../../../helpers';
import {
  GoalTypes,
  TPGoalDataCollectionMethod,
} from '../../../../../../components/layout/treatment-plan/goals/common/constants/general';
import { Themes } from '../../../../../../constants';
import {
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY4,
  FORTA_GRAY3,
  FORTA_WHITE,
  FORTA_ORANGE,
  FORTA_BLACK,
} from '../../../../../../utils/colors';
import {
  generateCreateTrialPayload,
  goalChipRenderer,
  getSelectedTechniqueOptions,
  sortTrialsByTargetOrder,
} from '../../../utils';
import PlusIcon from '../../../../../../components/svgs/PlusIcon';
import LinkedGoalIcon from '../../../../../../components/svgs/LinkedGoalIcon';

import type {
  IEvidenceBasedInterventionsUsed,
  IBehaviorGoalName,
  ISessionGoalTiming,
  ISessionGoalsAndTrials,
  ISessionPracticedTargetTrial,
  ITargetTrialInstanceDuration,
  ITargetTrialRateEvent,
  ITreatmentPlanGoal,
  ITreatmentPlanGoalTarget,
} from '../../../../../../utils/types';
import type { RootState } from '../../../../../../store';

type SelectableGoal = ITreatmentPlanGoal & { id: number; goal_name: IBehaviorGoalName };

function isSelectableGoal(goal: ITreatmentPlanGoal): goal is SelectableGoal {
  return goal.id !== undefined && goal.goal_name !== undefined;
}

interface GoalsPracticedListProps {
  isEditable: boolean;
  evidenceBasedInterventionsUsed: IEvidenceBasedInterventionsUsed;
  onUpdate: (goalsAndTrialsData: ISessionGoalsAndTrials) => void;
  showError: boolean;
  useNewUi: boolean;
}

function GoalsPracticedList({
  isEditable,
  evidenceBasedInterventionsUsed,
  showError,
  onUpdate,
  useNewUi,
}: GoalsPracticedListProps) {
  const { t } = useTranslation('sessionNotes');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const { current: session, trialLoading } = useSelector((state: RootState) => state.sessions);
  const { goals_and_trials: goalsPracticedAndSaved } = session ?? {};
  const [showAddGoalsDialog, setShowAddGoalsDialog] = useState<boolean>(false);
  // result from Tp goals api
  const [tpGoalList, setTpGoalList] = useState<ITreatmentPlanGoal[]>([]);
  // Initialised from goals_and_trials. later changed based on add / delete
  const [goalsAndTrialsData, setGoalsAndTrialsData] = useState<ISessionGoalsAndTrials | null>(null);
  // Ids of above
  const [goalsPracticedIdList, setGoalsPracticedIdList] = useState<number[]>([]);
  const [expandedGoalIds, setExpandedGoalIds] = useState<number[]>([]);
  const [isAllGoalsAdded, setIsAllGoalsAdded] = useState<boolean>(false);
  const [savingFailed, setSavingFailed] = useState<boolean>(false);
  const autoAddingTargetIdsRef = useRef<Set<number>>(new Set());

  const activeTpId = session?.active_treatment_plan_id ?? null;
  const techniqueOptions = useMemo(
    () => getSelectedTechniqueOptions(t, evidenceBasedInterventionsUsed),
    [evidenceBasedInterventionsUsed, t],
  );

  const buildAutoGoalTiming = useCallback(
    (): ISessionGoalTiming => ({
      timing_type: SessionGoalTimingType.Auto,
      start_offset_seconds: 0,
      end_offset_seconds: session?.expected_duration ?? 0,
      duration_seconds: session?.expected_duration ?? 0,
      order_index: 0,
    }),
    [session?.expected_duration],
  );

  useEffect(() => {
    // Session call is required for fetching updated goals_and_trial data, which is now path of session data
    dispatch(sessionActions.getSession({ sessionId: session?.id, getAccessTokenSilently }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!goalsAndTrialsData) return;

    onUpdate(goalsAndTrialsData);
    dispatch(
      sessionActions.updateState({
        key: 'current',
        data: { ...session, goals_and_trials: goalsAndTrialsData ?? {} },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalsAndTrialsData]);

  // Fill data from goals_and_trials
  useEffect(() => {
    if (!goalsPracticedAndSaved) return;
    setGoalsAndTrialsData(goalsPracticedAndSaved);
    setGoalsPracticedIdList(Object.keys(goalsPracticedAndSaved).map((key) => +key));
  }, [goalsPracticedAndSaved]);

  useEffect(() => {
    if (!useNewUi) return;
    setExpandedGoalIds((prev) => [
      ...prev.filter((goalId) => goalsPracticedIdList.includes(goalId)),
      ...goalsPracticedIdList.filter((goalId) => !prev.includes(goalId)),
    ]);
  }, [goalsPracticedIdList, useNewUi]);

  // Fetch TP goals
  useEffect(() => {
    if (!activeTpId || !session || !isEditable) {
      setTpGoalList([]);
      dispatch(goalActions.clearState());
      return;
    }

    (async () => {
      const response = await dispatch(
        goalActions.getGoals({
          treatmentPlanId: activeTpId,
          date: moment(session?.date_start).utc().format(),
          getAccessTokenSilently,
        }),
      );
      // set tp goals to state
      if (!response.error) {
        const goals = response.payload.filter(
          (goal: ITreatmentPlanGoal) => goal.goal_type !== GoalTypes.Caregiver,
        );
        setTpGoalList(goals);
      }
    })();

    return () => {
      dispatch(goalActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTpId, session?.date_start, isEditable]);

  // Disable add goals button if all goals are added and vice versa
  useEffect(() => {
    const activeGoalIds = tpGoalList.map((goal) => goal.id);
    setIsAllGoalsAdded(activeGoalIds.every((id) => goalsPracticedIdList.includes(id)));
  }, [tpGoalList, goalsPracticedIdList]);

  const handleUpdateGoalsPracticed = async (
    goalId: number,
    targets: ITreatmentPlanGoalTarget[],
  ) => {
    let trials: ISessionPracticedTargetTrial[] = [];

    const currentGoal = tpGoalList.find((goal) => goal.id === goalId);
    if (!currentGoal) return false;

    const durationTargets =
      currentGoal?.data_collection_method === TPGoalDataCollectionMethod.DURATION ? targets : [];

    const durationTrials = durationTargets.map(
      (target) =>
        ({
          target_id: target.id,
          expected_response: 0,
          occurrence: 0,
          attempts: 0,
          duration_events: [],
          target,
        }),
    );
    if (durationTrials.length > 0) {
      const res = await handleCreateTrials(
        durationTrials,
        currentGoal?.data_collection_method ?? null,
      );
      if (res.some((trial) => !trial)) return false;
      trials = [...trials, ...res.filter((res) => !!res)];
    }

    const rateTargets =
      currentGoal?.data_collection_method === TPGoalDataCollectionMethod?.RATE ? targets : [];

    const rateTrials = rateTargets.map(
      (target) =>
        ({
          target_id: target.id,
          expected_response: 0,
          occurrence: 0,
          attempts: 0,
          rate_events: [],
          target,
        }),
    );
    if (rateTrials.length > 0) {
      const res = await handleCreateTrials(rateTrials, currentGoal?.data_collection_method ?? null);
      if (res.some((trial) => !trial)) return false;
      trials = [...trials, ...res.filter((res) => !!res)];
    }

    // Instance trials need to be created initially with zero value
    const instanceTargets = targets.filter(
      (target) =>
        currentGoal?.data_collection_method === TPGoalDataCollectionMethod?.INSTANCES &&
        !target?.is_event_duration_required,
    );

    const instanceTrials = instanceTargets.map(
      (target) =>
        ({
          target_id: target.id,
          occurrence: 0,
          expected_response: 0,
          attempts: 0,
          target,
        }),
    );
    if (instanceTrials.length > 0) {
      const res = await handleCreateTrials(
        instanceTrials,
        currentGoal?.data_collection_method ?? null,
      );
      if (res.some((trial) => !trial)) return false;
      trials = [...res.filter((res) => !!res)];
    }

    const instancesWithDurtaionTargets = targets.filter(
      (target) =>
        currentGoal?.data_collection_method === TPGoalDataCollectionMethod?.INSTANCES &&
        target?.is_event_duration_required,
    );
    const instancesList: ITargetTrialInstanceDuration[] = [];
    const instancesWithDurtaionTrials = instancesWithDurtaionTargets.map(
      (target) =>
        ({
          target_id: target.id,
          occurrence: 0,
          expected_response: 0,
          attempts: 0,
          instances: instancesList,
          target,
        }),
    );
    if (instancesWithDurtaionTrials.length > 0) {
      const res = await handleCreateTrials(
        instancesWithDurtaionTrials,
        currentGoal?.data_collection_method ?? null,
      );
      if (res.some((trial) => !trial)) return false;
      trials = [...trials, ...res.filter((res) => !!res)];
    }

    const attemptsTargets =
      currentGoal?.data_collection_method === TPGoalDataCollectionMethod?.PERCENTAGE ? targets : [];
    const attemptsTrials = attemptsTargets.map(
      (target) =>
        ({
          target_id: target.id,
          attempts: 0,
          expected_response: 0,
          occurrence: 0,
          target,
        }),
    );
    if (attemptsTrials.length > 0) {
      const res = await handleCreateTrials(
        attemptsTrials,
        currentGoal?.data_collection_method ?? null,
      );
      if (res.some((trial) => !trial)) return false;
      trials = [...trials, ...res.filter((res) => !!res)];
    }

    const newGoalPracticed = {
      [goalId]: {
        goal_name: '',
        linked_goals: {},
        data_collection_method: currentGoal.data_collection_method ?? null,
        techniques: goalsAndTrialsData?.[goalId]?.techniques ?? [],
        timings:
          currentGoal.goal_type === GoalTypes.BehaviorReduction
            ? [buildAutoGoalTiming()]
            : (goalsAndTrialsData?.[goalId]?.timings ?? []),
        trials:
          goalsAndTrialsData && goalsAndTrialsData[goalId]
            ? sortTrialsByTargetOrder([...goalsAndTrialsData[goalId].trials, ...trials])
            : sortTrialsByTargetOrder(trials),
      },
    };
    setGoalsAndTrialsData((prev) => ({ ...prev, ...newGoalPracticed }));
    setGoalsPracticedIdList((prev) => (prev.includes(goalId) ? prev : [...prev, goalId]));
    return true;
  };

  const handleCreateTrials = async (
    trials: ISessionPracticedTargetTrial[],
    dataCollectionMethod: TPGoalDataCollectionMethod | null,
  ) => {
    return await Promise.all(
      trials.map(async (trial) => {
        const res = await fetchCreateTrial(trial, dataCollectionMethod);
        if (res?.error) {
          return;
        }
        return res?.payload;
      }),
    );
  };

  const fetchCreateTrial = async (
    trial: ISessionPracticedTargetTrial,
    dataCollectionMethod: TPGoalDataCollectionMethod | null,
  ) => {
    if (!trial || trial.id) return;

    // Prepare data to create trial
    const data = generateCreateTrialPayload(trial, dataCollectionMethod ?? null);
    // If percentage type

    return await dispatch(
      sessionActions.createTrial({
        sessionId: session?.id,
        data,
        getAccessTokenSilently,
      }),
    );
  };

  const handleDeleteGoal = async (goalId: number) => {
    if (!goalId || !goalsAndTrialsData || !goalsAndTrialsData[goalId]) {
      setGoalsPracticedIdList((prev) => prev.filter((id) => id !== goalId));
      return;
    }
    const trials = goalsAndTrialsData[goalId]?.trials;
    const trialsWhichFailedDeletion: ISessionPracticedTargetTrial[] = [];
    if (trials.length > 0) {
      await Promise.all(
        trials.map(async (trial) => {
          if (trial.id === undefined) return;
          const res = await fetchDeleteTrial(trial.id);
          if (res?.error) {
            trialsWhichFailedDeletion.push(trial);
            toast.error('Failed to delete data for target: ' + trial.target?.label);
          }
          return res;
        }),
      );
    }
    if (trialsWhichFailedDeletion?.length === 0) {
      setGoalsAndTrialsData((prev) => {
        if (!prev) return null;
        const { [goalId]: _removed, ...remainingGoals } = prev;
        return remainingGoals;
      });
      setGoalsPracticedIdList((prev) => prev.filter((id) => id !== goalId));
    }
  };

  const fetchDeleteTrial = async (trialId: number) => {
    if (!trialId || !session) return;

    return await dispatch(
      sessionActions.deleteTrial({
        sessionId: session.id,
        trialId,
        getAccessTokenSilently,
      }),
    );
  };

  const handleSaveTrial = async (goalId: number, targetId: number, trialDataDiff: any) => {
    if (!isEditable || !goalsAndTrialsData || !goalsAndTrialsData[goalId]) return;
    const trial = goalsAndTrialsData[goalId]?.trials.find((trial) => trial.target?.id === targetId);
    if (!trial?.id) return;

    const data = removeKey('utc_offset', removeKey('date_measured', trial));
    const isDataCollectionMethodRate: boolean =
      trialDataDiff && trialDataDiff.rate_events && trialDataDiff.rate_events.length > 0;
    if (!isDataCollectionMethodRate) {
      const trialData = {
        ...data,
        ...trialDataDiff,
      };
      handleUpdateTrial(trialData, goalId);
    } else {
      const rateEvent: ITargetTrialRateEvent = trialDataDiff?.rate_events[0];
      handleUpdateRateEvent(trial?.id, rateEvent);
    }
  };

  const handleUpdateRateEvent = async (trailId: number, rateEvent?: ITargetTrialRateEvent) => {
    try {
      if (rateEvent?.id) {
        await dispatch(
          sessionActions.updateRateEvent({
            getAccessTokenSilently,
            sessionId: session?.id,
            trailId,
            rateEventId: rateEvent.id,
            data: {
              date_of_event: moment(rateEvent?.date_of_event).toISOString(),
            },
          }),
        ).unwrap();
      } else if (rateEvent?.date_of_event) {
        await dispatch(
          sessionActions.createRateEvent({
            getAccessTokenSilently,
            sessionId: session?.id,
            trailId,
            data: {
              date_of_event: moment(rateEvent?.date_of_event).toISOString(),
            },
          }),
        ).unwrap();
      }
      return true;
    } catch {
      return false;
    }
  };
  const handleDeleteRateEvent = (trailId: number, rateEventId: number) => {
    dispatch(
      sessionActions.deleteRateEvent({
        getAccessTokenSilently,
        sessionId: session?.id,
        trailId,
        rateEventId,
      }),
    );
  };

  const handleUpdateTrial = async (trialData: ISessionPracticedTargetTrial, goalId: number) => {
    setSavingFailed(false);
    const res = await dispatch(
      sessionActions.updateTrial({
        sessionId: session?.id,
        data: trialData,
        getAccessTokenSilently,
      }),
    );
    if (res?.error) {
      setSavingFailed(true);
      return;
    }
    const updatedTrial = { ...res.payload, instances: res.payload.instances };

    if (goalsAndTrialsData?.[goalId])
      setGoalsAndTrialsData((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          [goalId]: {
            ...(prev[goalId] ?? {}),
            trials:
              prev[goalId]?.trials?.map((trial) =>
                trial.target?.id === trialData.target?.id ? updatedTrial : trial,
              ) ?? [],
          },
        };
      });
  };

  const handleDeleteTrial = async (goalId: number, trial: ISessionPracticedTargetTrial) => {
    if (!goalsAndTrialsData) return;

    if (trial.id) {
      const res = await dispatch(
        sessionActions.deleteTrial({
          sessionId: session?.id,
          trialId: trial.id,
          getAccessTokenSilently,
        }),
      );
      if (res.error) return;
    }

    setGoalsAndTrialsData((prev) => {
      if (!prev) return null;
      if (!prev[goalId]) return prev;

      const filtered = prev[goalId].trials.filter((t) => t.target?.id !== trial.target?.id);
      return {
        ...prev,
        [goalId]: {
          ...prev[goalId],
          trials: filtered,
        },
      };
    });
  };

  const handleUpdateTargets = async (goalId: number, targetIds: number[]) => {
    const goal = tpGoalList?.find((goal) => goal.id === goalId);
    if (!goal || !goalsAndTrialsData) return;

    const addedTargets = targetIds
      .filter(
        (targetId) =>
          !goalsAndTrialsData[goalId]?.trials.some((trial) => trial.target_id === targetId),
      )
      .map((targetId) =>
        goal.targets?.find((target) => target.id === targetId),
      )
      .filter((target): target is ITreatmentPlanGoalTarget => target !== undefined);

    if (addedTargets.length > 0) {
      handleUpdateGoalsPracticed(goalId, addedTargets);
    }
  };

  const handleUpdateGoalTechniques = useCallback((goalId: number, techniqueCodes: string[]) => {
    setGoalsAndTrialsData((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        [goalId]: {
          ...prev[goalId],
          techniques: techniqueCodes,
        },
      };
    });
  }, []);

  const handleUpdateGoalTimings = useCallback((goalId: number, timings: ISessionGoalTiming[]) => {
    setGoalsAndTrialsData((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        [goalId]: {
          ...prev[goalId],
          timings,
        },
      };
    });
  }, []);

  const handleGoalExpandedChange = (goalId: number, expanded: boolean) => {
    setExpandedGoalIds((prev) =>
      expanded ? [...prev, goalId] : prev.filter((expandedGoalId) => expandedGoalId !== goalId),
    );
  };

  useEffect(() => {
    if (!useNewUi || !isEditable || !goalsAndTrialsData || tpGoalList.length === 0) return;

    const addMissingBehaviorReductionTargets = async () => {
      for (const goal of tpGoalList.filter(
        (goal) => goal.is_active && goal.goal_type === GoalTypes.BehaviorReduction,
      )) {
        if (goal.id === undefined) continue;
        const goalId = goal.id;

        const missingTargets = (goal.targets ?? []).filter(
          (target) =>
            target.is_active &&
            target.id !== undefined &&
            !autoAddingTargetIdsRef.current.has(target.id) &&
            !goalsAndTrialsData[goalId]?.trials?.some(
              (trial) => trial.target?.id === target.id || trial.target_id === target.id,
            ),
        );

        if (missingTargets.length === 0) continue;

        missingTargets.forEach((target) => {
          if (target.id !== undefined) {
            autoAddingTargetIdsRef.current.add(target.id);
          }
        });
        const added = await handleUpdateGoalsPracticed(goalId, missingTargets);
        if (added) {
          missingTargets.forEach((target) => {
            if (target.id !== undefined) {
              autoAddingTargetIdsRef.current.delete(target.id);
            }
          });
        }
      }
    };

    void addMissingBehaviorReductionTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalsAndTrialsData, isEditable, tpGoalList, useNewUi]);

  useEffect(() => {
    if (!useNewUi || !goalsAndTrialsData || tpGoalList.length === 0) return;

    const autoGoalTiming = buildAutoGoalTiming();
    let nextGoalsAndTrialsData: ISessionGoalsAndTrials | null = null;

    for (const goal of tpGoalList) {
      if (goal.goal_type !== GoalTypes.BehaviorReduction || goal.id === undefined) continue;
      const currentGoalData = goalsAndTrialsData[goal.id];
      if (!currentGoalData) continue;

      const currentTiming = currentGoalData.timings?.[0];
      const isCurrentTimingInSync =
        currentGoalData.timings?.length === 1 &&
        currentTiming?.timing_type === autoGoalTiming.timing_type &&
        currentTiming?.start_offset_seconds === autoGoalTiming.start_offset_seconds &&
        currentTiming?.end_offset_seconds === autoGoalTiming.end_offset_seconds &&
        currentTiming?.duration_seconds === autoGoalTiming.duration_seconds;

      if (isCurrentTimingInSync) continue;

      nextGoalsAndTrialsData = nextGoalsAndTrialsData ?? { ...goalsAndTrialsData };
      nextGoalsAndTrialsData[goal.id] = {
        ...currentGoalData,
        timings: [autoGoalTiming],
      };
    }

    if (nextGoalsAndTrialsData) {
      setGoalsAndTrialsData(nextGoalsAndTrialsData);
    }
  }, [buildAutoGoalTiming, goalsAndTrialsData, tpGoalList, useNewUi]);

  const visibleGoalIds = goalsPracticedIdList.filter(
    (goalId) =>
      tpGoalList?.find((goal) => goal?.id === goalId)?.is_active ||
      (goalsAndTrialsData?.[goalId]?.trials.length ?? 0) > 0,
  );

  return (
    <Box>
      {trialLoading && <FProgressBar />}
      <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
        {(!goalsPracticedIdList || goalsPracticedIdList.length === 0) && (
          <FHWarning
            sxText={{
              ml: 1,
              minWidth: '300px',
              color: showError ? FORTA_ERROR : FORTA_BLACK,
              width: 27,
              height: 27,
            }}
            iconColor={showError ? FORTA_ERROR : FORTA_ORANGE}
            sxContainer={{
              mt: 1.5,
              ml: -0.5,
              backgroundColor: 'transparent',
              p: 0,
            }}
            bold
            content={t('goalsPracticed.pleaseSelectAtLeastOneGoal')}
          />
        )}
        {goalsAndTrialsData &&
          visibleGoalIds.map((goalId) => {
            const goal = tpGoalList.find((candidate) => candidate.id === goalId);
            const savedGoal = goalsAndTrialsData[goalId];
            if (!savedGoal) return null;
            if (isEditable && !goal) return null;

            return <GoalPracticed
              isEditable={isEditable}
              sessionId={session?.id}
              colorTheme={Themes.Primary}
              key={goalId}
              useNewUi={useNewUi}
              expanded={!useNewUi || expandedGoalIds.includes(goalId)}
              trials={sortTrialsByTargetOrder(savedGoal.trials)}
              goalsPracticedIdList={goalsPracticedIdList}
              goal={
                goal ?? {
                  id: goalId,
                  goal_name: { label: savedGoal.goal_name ?? '' },
                  goal_type: savedGoal.goal_type,
                  data_collection_method: savedGoal.data_collection_method,
                  linked_goals: savedGoal.linked_goals,
                  is_active: true,
                  targets: [],
                }
              }
              dataCollectionMethod={
                (isEditable
                  ? goal?.data_collection_method
                  : savedGoal.data_collection_method) || null
              }
              dateStart={session?.date_start}
              expectedDuration={session?.expected_duration}
              savedGoal={savedGoal}
              techniqueOptions={techniqueOptions}
              showError={showError}
              savingFailed={savingFailed}
              savingInProgress={trialLoading}
              onAddSuggestedGoal={(suggestedGoalId) => {
                if (goalsPracticedIdList.indexOf(suggestedGoalId) < 0) {
                  handleUpdateGoalsPracticed(suggestedGoalId, []);
                }
              }}
              onDelete={() => handleDeleteGoal(goalId)}
              onUpdateTrial={(targetId: number, trialDataDiff: any) =>
                handleSaveTrial(goalId, targetId, trialDataDiff)
              }
              onUpdateTechniques={handleUpdateGoalTechniques}
              onUpdateTimings={handleUpdateGoalTimings}
              onDeleteTrial={(trial: ISessionPracticedTargetTrial) =>
                handleDeleteTrial(goalId, trial)
              }
              onUpdateTargets={(targetIds: number[]) => handleUpdateTargets(goalId, targetIds)}
              onDeleteRateEvent={(trialId: number, rateEventId: number) =>
                handleDeleteRateEvent(trialId, rateEventId)
              }
              onUpdateRateEvent={(trialId: number, rateEvent: ITargetTrialRateEvent) =>
                handleUpdateRateEvent(trialId, rateEvent)
              }
              onExpandedChange={(expanded) => handleGoalExpandedChange(goalId, expanded)}
            />;
          })}
        {isEditable && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <FHButton
              startIcon={<PlusIcon color={isAllGoalsAdded ? FORTA_GRAY3 : FORTA_GRAY4} />}
              secondary
              disabled={isAllGoalsAdded}
              data-testid={'sessionAddGoal'}
              onClick={() => setShowAddGoalsDialog(true)}
            >
              <Typography fontWeight={700}>Add Goals</Typography>
            </FHButton>
          </Box>
        )}
      </Box>
      {isEditable && (
        <FHItemSelectDialog
          width={isMobile ? '100%' : '50%'}
          title="Add goals"
          submitText="Add"
          submitButtonStyles={{ width: '97px' }}
          showGoalTypeOptions={true}
          itemList={tpGoalList
            .filter((goal) => goal.is_active)
            .filter((goal) => !useNewUi || goal.goal_type !== GoalTypes.BehaviorReduction)
            .filter(isSelectableGoal)
            .filter((goal) => goalsPracticedIdList.indexOf(goal.id) < 0) //to hide already practiced goal from add goals dialog
            .map((goal) => {
              const linkedBehaviorReductionGoal = goal?.linked_goals?.behavior_reduction
                ? goal?.linked_goals?.behavior_reduction[0]
                : null;
              return {
                id: goal.id,
                label: capitalizeFirstLetter(goal.goal_name.label),
                checked: goalsPracticedIdList.indexOf(goal.id) >= 0,
                dataTestId: `sessionGoal-${capitalizeFirstLetter(
                  goal.goal_name.label.split(' ').join(''),
                )}`,
                goalType: goal.goal_type, // Add this line
                contentRenderer: (id: any, label: any, checked: any) =>
                  linkedBehaviorReductionGoal ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: { xs: 2, sm: 0 },
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                      >
                        <Typography
                          sx={{
                            fontSize: {
                              xs: 15,
                              md: 21,
                            },
                            fontWeight: 500,
                            color: checked ? FORTA_WHITE : FORTA_BLUE.primary,
                          }}
                        >
                          {capitalizeFirstLetter(goal.goal_name.label)}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                          }}
                        >
                          <LinkedGoalIcon
                            color={checked ? FORTA_WHITE : FORTA_BLUE.primary}
                            width={20}
                            height={20}
                          />
                          <Typography
                            sx={{
                              fontSize: {
                                xs: 12,
                                md: 14,
                              },
                              fontWeight: 500,
                              color: checked ? FORTA_WHITE : FORTA_BLUE.light1,
                            }}
                          >
                            {capitalizeFirstLetter(
                              linkedBehaviorReductionGoal.goal_name?.label ?? '',
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <FHChip
                        contentRenderer={() =>
                          goalChipRenderer(goal, Boolean(linkedBehaviorReductionGoal))
                        }
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: 14,
                              md: 21,
                            },
                            fontWeight: 500,
                            color: checked ? FORTA_WHITE : FORTA_BLUE.primary,
                          }}
                        >
                          {goal.goal_name.label}
                        </Typography>
                      </Box>
                      <FHChip
                        contentRenderer={() =>
                          goalChipRenderer(goal, Boolean(linkedBehaviorReductionGoal))
                        }
                      />
                    </Box>
                  ),
              };
            })}
          onSave={(listOfGoalIdsToBeAdded) => {
            listOfGoalIdsToBeAdded.forEach((goalId) => {
              if (goalsPracticedIdList.indexOf(goalId) < 0) {
                handleUpdateGoalsPracticed(goalId, []);
              }
            });
            setShowAddGoalsDialog(false);
          }}
          onClose={() => {
            setShowAddGoalsDialog(false);
          }}
          open={showAddGoalsDialog}
        />
      )}
    </Box>
  );
}

export default GoalsPracticedList;
